/**
 * Edição da verba DIÁRIA de campanha ou conjunto.
 *
 * Verba total (lifetime) fica de fora de propósito: mudar o total de uma
 * campanha com datas exige recalcular o período, e o Gerenciador faz isso
 * com mais contexto. O diálogo diz onde ir.
 */

import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { centavosParaReais, moeda, reaisParaCentavos } from "@/lib/meta-ads-format";

export function BudgetDialog({
  open,
  onOpenChange,
  objectType,
  objectId,
  nome,
  currentDailyCents,
  usaVerbaTotal,
  currency,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  objectType: "campaign" | "adset";
  objectId: string;
  nome: string;
  currentDailyCents: number | null | undefined;
  /** Objeto com lifetime_budget: a verba diária não se aplica. */
  usaVerbaTotal: boolean;
  currency: string | null;
  onSaved: () => void;
}) {
  const [texto, setTexto] = useState(centavosParaReais(currentDailyCents));
  useEffect(() => {
    if (open) setTexto(centavosParaReais(currentDailyCents));
  }, [open, currentDailyCents]);

  const limits = trpc.metaAds.limits.get.useQuery(undefined, { staleTime: 5 * 60_000, enabled: open });
  const cap = limits.data?.budgetCapCents ?? null;
  const min = limits.data?.minDailyBudgetCents ?? 100;

  const campanha = trpc.metaAds.campaigns_write.update.useMutation();
  const conjunto = trpc.metaAds.adsets_write.update.useMutation();
  const salvando = campanha.isPending || conjunto.isPending;

  const cents = reaisParaCentavos(texto);
  const erro =
    texto.trim() === ""
      ? null
      : cents == null
        ? "Valor inválido."
        : cents < min
          ? `Mínimo de ${moeda(min, currency)} por dia.`
          : cap != null && cents > cap
            ? `Acima do limite do workspace (${moeda(cap, currency)}/dia).`
            : null;

  const salvar = async () => {
    if (cents == null || erro) return;
    try {
      if (objectType === "campaign") await campanha.mutateAsync({ campaignId: objectId, dailyBudgetCents: cents });
      else await conjunto.mutateAsync({ adsetId: objectId, dailyBudgetCents: cents });
      toast.success("Verba diária atualizada.");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(toUserFriendlyError(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Verba diária</DialogTitle>
          <DialogDescription className="truncate">{nome}</DialogDescription>
        </DialogHeader>

        {usaVerbaTotal ? (
          <p className="text-sm text-muted-foreground">
            Este {objectType === "campaign" ? "campanha" : "conjunto"} usa verba <strong>total</strong> (para o período), não diária. Para
            alterar, use o Gerenciador de Anúncios — lá a Meta recalcula o ritmo pelo prazo que falta.
          </p>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="verba-diaria">Valor por dia ({currency ?? "BRL"})</Label>
            <Input
              id="verba-diaria"
              inputMode="decimal"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Ex.: 50,00"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              {cap != null ? `Limite do workspace: ${moeda(cap, currency)}/dia.` : "Sem limite definido para o workspace."}{" "}
              Mínimo {moeda(min, currency)}.
            </p>
            {erro && <p className="text-xs text-red-600">{erro}</p>}
            {cents != null && !erro && currentDailyCents != null && cents > currentDailyCents && (
              <p className="text-xs text-amber-700">
                Se estiver no ar, o gasto sobe a partir de hoje.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={salvando}>
            Cancelar
          </Button>
          {!usaVerbaTotal && (
            <Button onClick={() => void salvar()} disabled={salvando || cents == null || !!erro}>
              {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
